import axios from 'axios'

import PageElementGroupEditor from './Editors/PageElementGroupEditor.vue'
import PageElementHeadingEditor from './Editors/PageElementHeadingEditor.vue'
import PageElementImageEditor from './Editors/PageElementImageEditor.vue'
import PageElementLinkButtonEditor from './Editors/PageElementLinkButtonEditor.vue'
import PageElementProductEditor from './Editors/PageElementProductEditor.vue'
import PageElementProductsEditor from './Editors/PageElementProductsEditor.vue'
import PageElementSlideshowEditor from './Editors/PageElementSlideshowEditor.vue'
import PageElementTextEditor from './Editors/PageElementTextEditor.vue'
import PageElementFaq from './Elements/PageElementFaq.vue'
import PageElementGroup from './Elements/PageElementGroup.vue'
import PageElementHeading from './Elements/PageElementHeading.vue'
import PageElementImage from './Elements/PageElementImage.vue'
import PageElementLinkButton from './Elements/PageElementLinkButton.vue'
import PageElementPlaceholder from './Elements/PageElementPlaceholder.vue'
import PageElementProduct from './Elements/PageElementProduct.vue'
import PageElementProducts from './Elements/PageElementProducts.vue'
import PageElementSlideshow from './Elements/PageElementSlideshow.vue'
import PageElementText from './Elements/PageElementText.vue'
import PageElementFaqViewer from './Viewers/PageElementFaqViewer.vue'
import PageElementGroupViewer from './Viewers/PageElementGroupViewer.vue'
import PageElementHeadingViewer from './Viewers/PageElementHeadingViewer.vue'
import PageElementImageViewer from './Viewers/PageElementImageViewer.vue'
import PageElementLinkButtonViewer from './Viewers/PageElementLinkButtonViewer.vue'
import PageElementPlaceholderViewer from './Viewers/PageElementPlaceholderViewer.vue'
import PageElementProductsViewer from './Viewers/PageElementProductsViewer.vue'
import PageElementProductViewer from './Viewers/PageElementProductViewer.vue'
import PageElementSlideshowViewer from './Viewers/PageElementSlideshowViewer.vue'
import PageElementTextViewer from './Viewers/PageElementTextViewer.vue'

import type { Component, InjectionKey } from 'vue'

import IAntDesignGroupOutlined from '~icons/ant-design/group-outlined'
import IBiImage from '~icons/bi/image'
import IF7Tickets from '~icons/f7/tickets'
import IHeroiconsLink from '~icons/heroicons/link'
import IHeroiconsTicket from '~icons/heroicons/ticket'
import IMiText from '~icons/mi/text'
import IPhPlaceholder from '~icons/ph/placeholder'
import IPhQuestion from '~icons/ph/question'
import IPhSlideshow from '~icons/ph/slideshow'
import ITablerHeading from '~icons/tabler/heading'

export const ID_TO_PAGE_ELEMENT_KEY = Symbol() as InjectionKey<Map<string, PageElement>>
export const HOVERED_ELEMENTS_KEY = Symbol() as InjectionKey<Set<PageElement>>
export const HOVERED_ELEMENT_KEY = Symbol() as InjectionKey<Ref<PageElement | null>>
export const SELECTED_ELEMENT_KEY = Symbol() as InjectionKey<Ref<PageElement | null>>
export const ROOT_CONTAINER_KEY = Symbol() as InjectionKey<Ref<HTMLDivElement | undefined>>
export const FAQ_ENTRIES_KEY = Symbol() as InjectionKey<Ref<App.Data.Tenant.TenantFaqEntryData[]>>
export const PRODUCT_CATEGORIES_KEY = Symbol() as InjectionKey<
  Ref<App.Data.Tenant.TenantProductCategoryData[]>
>
export const PRODUCTS_KEY = Symbol() as InjectionKey<Ref<App.Data.Tenant.TenantProductData[]>>

export type PageElementGroupData = {
  sections: PageGroupSection[]
  renderAsCard: boolean
}

export type PageGroupSection = {
  parent: PageElementOfType<'group'>
  id: string
  blocks: {
    element: PageElement
    size: number
    alignment: 'stretch' | 'start' | 'center' | 'end'
  }[]
}

export type PageElement = {
  parent: PageGroupSection | null
  id: string
} & (
  | {
      type: 'group'
      data: PageElementGroupData
    }
  | {
      type: 'heading'
      data: {
        text: string
        size: number
      }
    }
  | {
      type: 'text'
      data: {
        text: string
      }
    }
  | {
      type: 'image'
      data: {
        url: string | null
      }
    }
  | {
      type: 'placeholder'
      data: null
    }
  | {
      type: 'linkButton'
      data: {
        label: string | null
        target:
          | {
              type: 'internal'
              route: string | null
            }
          | {
              type: 'external'
              url: string | null
            }
        color: 'primary' | 'accent' | 'white'
        secondary: boolean
      }
    }
  | {
      type: 'faq'
      data: null
    }
  | {
      type: 'products'
      data: {
        categoryId: number | null
      }
    }
  | {
      type: 'product'
      data: {
        productId: number | null
      }
    }
  | {
      type: 'slideshow'
      data: {
        urls: string[]
      }
    }
)

export type PageElementType = PageElement['type']
export type PageElementOfType<T extends PageElementType> = PageElement & { type: T }
export type PageElementDataOfType<T extends PageElementType> = PageElementOfType<T>['data']

type OmitBetter<T, K extends keyof T> = T extends unknown ? Omit<T, K> : never
export type SerializablePageElement =
  | Exclude<OmitBetter<PageElement, 'parent'>, { type: 'group' }>
  | {
      id: string
      type: 'group'
      data: SerializablePageElementGroupData
    }

type SerializablePageElementGroupData = Omit<PageElementGroupData, 'sections'> & {
  sections: SerializablePageGroupSection[]
}
type SerializablePageGroupSection = Omit<PageGroupSection, 'parent' | 'blocks'> & {
  blocks: {
    element: SerializablePageElement
    size: number
    alignment?: 'stretch' | 'start' | 'center' | 'end' // Optional because this field was added later
  }[]
}

export const pageElementSpecs: {
  [K in PageElementType]: {
    label: string
    icon: Component
    hidden?: true
    renderer: () => Component
    viewer: () => Component
    editor?: () => Component
    createNewData: () => PageElementOfType<K>['data']
  }
} = {
  group: {
    label: 'Gruppe',
    icon: IAntDesignGroupOutlined,
    renderer: () => PageElementGroup as Component,
    editor: () => PageElementGroupEditor as Component,
    viewer: () => PageElementGroupViewer as Component,
    createNewData: () => ({
      sections: [],
      renderAsCard: false,
    }),
  },
  heading: {
    label: 'Überschrift',
    icon: ITablerHeading,
    renderer: () => PageElementHeading as Component,
    editor: () => PageElementHeadingEditor as Component,
    viewer: () => PageElementHeadingViewer as Component,
    createNewData: () => ({
      text: '',
      size: 1,
    }),
  },
  text: {
    label: 'Text',
    icon: IMiText,
    renderer: () => PageElementText as Component,
    editor: () => PageElementTextEditor as Component,
    viewer: () => PageElementTextViewer as Component,
    createNewData: () => ({
      text: '',
    }),
  },
  image: {
    label: 'Bild',
    icon: IBiImage,
    renderer: () => PageElementImage as Component,
    editor: () => PageElementImageEditor as Component,
    viewer: () => PageElementImageViewer as Component,
    createNewData: () => ({
      url: null,
    }),
  },
  linkButton: {
    label: 'Link-Knopf',
    icon: IHeroiconsLink,
    renderer: () => PageElementLinkButton as Component,
    editor: () => PageElementLinkButtonEditor as Component,
    viewer: () => PageElementLinkButtonViewer as Component,
    createNewData: () => ({
      label: null,
      target: {
        type: 'external',
        url: null,
      },
      color: 'primary',
      secondary: false,
    }),
  },
  placeholder: {
    label: 'Platzhalter',
    icon: IPhPlaceholder,
    renderer: () => PageElementPlaceholder as Component,
    viewer: () => PageElementPlaceholderViewer as Component,
    createNewData: () => null,
  },
  faq: {
    label: 'FAQ',
    icon: IPhQuestion,
    renderer: () => PageElementFaq as Component,
    viewer: () => PageElementFaqViewer as Component,
    createNewData: () => null,
  },
  products: {
    label: 'Ticketangebot',
    icon: IF7Tickets,
    renderer: () => PageElementProducts as Component,
    editor: () => PageElementProductsEditor as Component,
    viewer: () => PageElementProductsViewer as Component,
    createNewData: () => ({
      categoryId: null,
    }),
  },
  product: {
    label: 'Ticket',
    icon: IHeroiconsTicket,
    renderer: () => PageElementProduct as Component,
    editor: () => PageElementProductEditor as Component,
    viewer: () => PageElementProductViewer as Component,
    createNewData: () => ({
      productId: null,
    }),
  },
  slideshow: {
    label: 'Diashow',
    icon: IPhSlideshow,
    renderer: () => PageElementSlideshow as Component,
    editor: () => PageElementSlideshowEditor as Component,
    viewer: () => PageElementSlideshowViewer as Component,
    createNewData: () => ({
      urls: [],
    }),
  },
}

export function pageElementToSerializablePageElement(
  element: PageElement,
): SerializablePageElement {
  // eslint-disable-next-line unicorn/prefer-ternary
  if (element.type === 'group') {
    return {
      id: element.id,
      type: 'group',
      data: {
        sections: element.data.sections.map((section) => ({
          id: section.id,
          blocks: section.blocks.map((block) => ({
            element: pageElementToSerializablePageElement(block.element),
            size: block.size,
            alignment: block.alignment,
          })),
        })),
        renderAsCard: element.data.renderAsCard,
      },
    }
  } else {
    // Handle non-group elements
    return {
      id: element.id,
      type: element.type,
      data: element.data,
    } as SerializablePageElement
  }
}

export function serializablePageElementToPageElement(
  element: SerializablePageElement,
  parent: PageGroupSection | null = null,
): PageElement {
  if (element.type === 'group') {
    const groupElement: PageElementOfType<'group'> = {
      id: element.id,
      type: 'group',
      data: {
        sections: [],
        renderAsCard: element.data.renderAsCard,
      },
      parent,
    }

    // Populate the sections
    groupElement.data.sections = element.data.sections.map((section) => {
      const newSection: PageGroupSection = {
        parent: groupElement,
        id: section.id,
        blocks: section.blocks.map((block) => ({
          element: serializablePageElementToPageElement(block.element, null), // Recursively convert elements
          size: block.size,
          alignment: block.alignment ?? 'stretch',
        })),
      }

      // Set the parent of each block's element
      for (const block of newSection.blocks) {
        block.element.parent = newSection
      }

      return newSection
    })

    return groupElement
  } else {
    // Handle non-group elements
    return {
      id: element.id,
      type: element.type,
      data: element.data,
      parent,
    } as PageElement
  }
}

export function traversePageElement(
  element: PageElement,
  callback: (element: PageElement) => void,
): void {
  callback(element)

  if (element.type === 'group') {
    for (const section of element.data.sections) {
      for (const block of section.blocks) {
        traversePageElement(block.element, callback)
      }
    }
  }
}

export function removeElement(
  element: PageElement,
  idToElement: Map<string, PageElement>,
  hoveredElements: Set<PageElement>,
) {
  if (!element.parent) {
    return
  }

  const section = element.parent
  const index = section.blocks.findIndex((x) => x.element === element)
  section.blocks.splice(index, 1)

  for (const block of section.blocks) {
    block.size = 6 / section.blocks.length // Guaranteed to be an integer
  }

  if (section.blocks.length === 0) {
    const sectionIndex = section.parent.data.sections.indexOf(section)
    section.parent.data.sections.splice(sectionIndex, 1)
  }

  idToElement.delete(element.id)
  hoveredElements.delete(element)
}

const resizableImageTypes = new Set(['image/jpeg', 'image/png', 'image/webp'])

export async function processAndUploadImage(
  file: File,
  tenant: App.Data.Tenant.PublicTenantData,
  onProgress?: (progress: number) => void,
): Promise<string | null> {
  if (resizableImageTypes.has(file.type)) {
    file = await resizeImage(file)
  }

  const formData = new FormData()
  formData.append('file', file)

  try {
    const response = await axios.post<{ url: string }>(
      route('admin.tenants.landing-page.upload-image', { tenant }),
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress(progressEvent) {
          onProgress?.(progressEvent.progress ?? 0)
        },
      },
    )
    return response.data.url
  } catch (error) {
    console.error(error)
    return null
  }
}

async function resizeImage(file: File): Promise<File> {
  const image = new Image()
  image.src = URL.createObjectURL(file)
  await new Promise((resolve) => {
    image.addEventListener('load', resolve, { once: true })
  })

  // Compress the image
  const scaleFactor = Math.min(1536 / Math.max(image.naturalWidth, image.naturalHeight), 1)
  const canvas = document.createElement('canvas')
  canvas.width = Math.floor(image.naturalWidth * scaleFactor)
  canvas.height = Math.floor(image.naturalHeight * scaleFactor)
  const context = canvas.getContext('2d')!
  context.drawImage(image, 0, 0, canvas.width, canvas.height)

  const resizedBlob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, 'image/webp', 0.8)
  })
  if (!resizedBlob) {
    throw new Error('Failed to compress image')
  }

  const newFileName = file.name.replace(/\.\w+$/, '.webp')
  return new File([resizedBlob], newFileName, {
    type: 'image/webp',
    lastModified: Date.now(),
  })
}
